// AccesoExternoPage.js — Entrada desde otra plataforma con un solo enlace.
//
// Ruta: /acceso/:slug/:token?full_name=…&email=…&phone=…&ciudad=…
//
// La página no pide nada: toma lo que trae la URL, lo canjea en el backend por
// una sesión y deja a la persona dentro del curso. Solo se ve mientras eso
// ocurre, o si el enlace no es válido.
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { accesoExternoService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useProject } from '../contexts/ProjectContext';
import { BACKEND_BASE } from '../services/api';

const Pantalla = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.1rem;
  padding: 2rem 1.4rem;
  text-align: center;
  background: ${p => p.$bg || '#f5f7fb'};
  color: #0b1f3a;
  font-family: 'Segoe UI', system-ui, sans-serif;
`;

const Logo = styled.img`
  max-height: 64px;
  width: auto;
  margin-bottom: 0.4rem;
`;

const Titulo = styled.h1`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$color || '#012965'};
`;

const Texto = styled.p`
  margin: 0;
  max-width: 460px;
  color: #5b6779;
  line-height: 1.55;
`;

const Rueda = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(1, 41, 101, 0.15);
  border-top-color: ${p => p.$color || '#012965'};
  animation: girar 0.9s linear infinite;
  @keyframes girar { to { transform: rotate(360deg); } }
  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Boton = styled.button`
  margin-top: 0.6rem;
  border: none;
  border-radius: 8px;
  padding: 0.7rem 1.6rem;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  background: ${p => p.$bg || '#012965'};
  color: #fff;
  &:hover { filter: brightness(1.08); }
`;

const AccesoExternoPage = () => {
  const { slug, token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { proyecto } = useProject();
  const [error, setError] = useState('');
  // En desarrollo el efecto corre dos veces (StrictMode); sin esto se
  // registrarían dos accesos por cada clic.
  const yaFue = useRef(false);

  const color = proyecto?.color_primario || '#012965';
  const logo = proyecto?.logo ? `${BACKEND_BASE}/${proyecto.logo}` : null;

  useEffect(() => {
    if (yaFue.current) return;
    yaFue.current = true;

    const params = {};
    searchParams.forEach((valor, clave) => { params[clave] = valor; });

    accesoExternoService.entrar(slug, token, params)
      .then(res => {
        const d = res.data || {};
        if (!d.success) { setError(d.message || 'No se pudo completar el acceso'); return; }
        login({ user: d.user, token: d.token });
        // `replace`: la URL lleva los datos de la persona, así que no debe
        // quedar en el historial ni volverse con el botón atrás.
        navigate(d.destino || '/contenidos', { replace: true });
      })
      .catch(err => {
        setError(err.response?.data?.message || 'No se pudo completar el acceso');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, token]);

  if (error) {
    return (
      <Pantalla>
        {logo && <Logo src={logo} alt="" />}
        <Titulo $color={color}>No pudimos abrirte la sesión</Titulo>
        <Texto>{error}</Texto>
        <Boton $bg={color} onClick={() => navigate('/')}>Ir al inicio</Boton>
      </Pantalla>
    );
  }

  return (
    <Pantalla>
      {logo && <Logo src={logo} alt="" />}
      <Rueda $color={color} />
      <Titulo $color={color}>Preparando tu acceso</Titulo>
      <Texto>Un momento: estamos abriendo tu sesión y llevándote al curso.</Texto>
    </Pantalla>
  );
};

export default AccesoExternoPage;
